import { AlertCircle, CheckCircle2, Clock, MessageSquare } from "lucide-react";
import type { ComponentType } from "react";
import { useSearchParams } from "react-router-dom";

import { BachecaSidePanel } from "@/components/BachecaSidePanel";
import { EmptyState } from "@/components/EmptyState";
import { PageContainer } from "@/components/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useBacheca } from "@/hooks/useBacheca";
import { useMe } from "@/hooks/useMe";
import { cn } from "@/lib/utils";
import type {
  BachecaTipo,
  BachecaVoce,
  ParteResponsabile,
} from "@/types/api";

const TIPO_LABEL: Record<BachecaTipo, string> = {
  nota: "nota",
  decisione: "decisione",
  blocco: "blocco",
};

const TIPO_ICON: Record<BachecaTipo, ComponentType<{ className?: string }>> = {
  nota: MessageSquare,
  decisione: AlertCircle,
  blocco: AlertCircle,
};

const PARTE_LABEL: Record<ParteResponsabile, string> = {
  maitime: "in carico Maitime",
  misterpet: "in carico Mister Pet",
  entrambi: "decisione condivisa",
};

function formatData(iso: string): string {
  return new Date(iso).toLocaleDateString("it-IT", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function BachecaPage() {
  const { data: me } = useMe();
  const parteUtente: ParteResponsabile = me?.parte ?? "misterpet";
  const [searchParams, setSearchParams] = useSearchParams();
  const voceIdSelezionata = searchParams.get("voce");

  function apriVoce(voceId: string) {
    const next = new URLSearchParams(searchParams);
    next.set("voce", voceId);
    setSearchParams(next, { replace: false });
  }
  function chiudiVoce() {
    const next = new URLSearchParams(searchParams);
    next.delete("voce");
    setSearchParams(next, { replace: false });
  }

  const { data: voci = [], isLoading } = useBacheca();

  const aperte = voci.filter((v) => v.stato === "aperto" || v.stato === "in_corso");
  const chiuse = voci.filter((v) => v.stato === "chiuso");
  const voceSelezionata = voceIdSelezionata
    ? voci.find((v) => v.id === voceIdSelezionata) ?? null
    : null;

  return (
    <PageContainer>
      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">Sezione</p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">Bacheca</h1>
        <p className="text-sm text-muted-foreground max-w-2xl pt-1">
          Note, decisioni e blocchi del progetto. Le voci aperte indicano cosa serve per
          procedere; quelle chiuse sono il registro storico delle decisioni prese. Clicca
          una voce per leggere il thread o aggiungere un messaggio.
        </p>
      </header>

      <section className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-base font-semibold inline-flex items-center gap-2">
            Aperte
            {!isLoading && (
              <span className="text-xs font-normal text-muted-foreground font-mono">
                {aperte.length}
              </span>
            )}
          </h2>
        </div>
        {isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : aperte.length === 0 ? (
          <EmptyState
            icon={CheckCircle2}
            title="Nessuna voce aperta"
            description="Al momento non ci sono note, decisioni o blocchi che richiedano attenzione."
          />
        ) : (
          <div className="rounded-lg border bg-card divide-y">
            {aperte.map((v) => (
              <BachecaRow
                key={v.id}
                voce={v}
                selected={v.id === voceIdSelezionata}
                onOpen={() => apriVoce(v.id)}
              />
            ))}
          </div>
        )}
      </section>

      {chiuse.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-base font-semibold inline-flex items-center gap-2">
            Chiuse
            <span className="text-xs font-normal text-muted-foreground font-mono">
              {chiuse.length}
            </span>
          </h2>
          <div className="rounded-lg border bg-card divide-y">
            {chiuse.map((v) => (
              <BachecaRow
                key={v.id}
                voce={v}
                selected={v.id === voceIdSelezionata}
                onOpen={() => apriVoce(v.id)}
                closed
              />
            ))}
          </div>
        </section>
      )}

      <BachecaSidePanel
        voce={voceSelezionata}
        parteUtente={parteUtente}
        mode={parteUtente === "maitime" ? "admin" : "cliente"}
        onClose={chiudiVoce}
      />
    </PageContainer>
  );
}

function BachecaRow({
  voce,
  selected,
  onOpen,
  closed = false,
}: {
  voce: BachecaVoce;
  selected: boolean;
  onOpen: () => void;
  closed?: boolean;
}) {
  const Icon = TIPO_ICON[voce.tipo];
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "w-full flex gap-3 items-start px-4 py-3 text-sm text-left transition-colors",
        "hover:bg-muted/40",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-inset",
        selected && "bg-accent/40 border-l-2 border-l-primary",
        closed && "opacity-70"
      )}
    >
      <Icon
        className={cn(
          "size-4 shrink-0 mt-0.5",
          closed ? "text-muted-foreground" : "text-warning"
        )}
      />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <p className="font-medium leading-snug">{voce.titolo}</p>
          <Badge variant="outline" className="text-[10px] uppercase font-mono">
            {TIPO_LABEL[voce.tipo]}
          </Badge>
          {voce.stato === "in_corso" && (
            <Badge variant="outline" className="text-[10px] inline-flex items-center gap-1">
              <Clock className="size-2.5" />
              in corso
            </Badge>
          )}
          {voce.ha_messaggi_non_letti && (
            <span
              className="size-2 rounded-full bg-destructive shrink-0"
              title="Messaggi non letti"
            />
          )}
        </div>
        {voce.corpo && (
          <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
            {voce.corpo}
          </p>
        )}
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground pt-0.5 flex-wrap">
          <span>{PARTE_LABEL[voce.parte_responsabile]}</span>
          {voce.n_messaggi > 0 && (
            <>
              <span>·</span>
              <span className="inline-flex items-center gap-1">
                <MessageSquare className="size-3" />
                {voce.n_messaggi} {voce.n_messaggi === 1 ? "messaggio" : "messaggi"}
              </span>
            </>
          )}
          <span>·</span>
          <span>aperta {formatData(voce.created_at)}</span>
          {voce.chiuso_il && (
            <>
              <span>·</span>
              <span>chiusa {formatData(voce.chiuso_il)}</span>
            </>
          )}
        </div>
        {closed && voce.motivo_chiusura && (
          <p className="text-xs text-muted-foreground italic pt-1">
            "{voce.motivo_chiusura}"
          </p>
        )}
      </div>
    </button>
  );
}
